import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowRight,
  CheckCircle2,
  Target,
  Search,
  FileCheck,
  RefreshCw,
  Handshake,
  Phone,
  Upload,
  Users,
  Check,
  X,
  ChevronDown
} from 'lucide-react';

export const SuccessPacksPage = () => {
  const [activeTab, setActiveTab] = useState('sourcing');
  const [openFaq, setOpenFaq] = useState(0);

  const packs = [
    {
      id: 'sourcing',
      slug: 'sourcing-partnership-pack',
      number: '01',
      name: 'Sourcing & Partnership Pack',
      tagline: 'Build Your Manufacturing and Distribution Backbone.',
      color: '#A07050',
      icon: Handshake,
      includes: [
        'Vetted GMP-certified manufacturer leads',
        'Licensed importer and distributor leads',
        'Manufacturing and distribution agreement support',
        'GMP application assistance',
        'New-site application assistance',
        'Partnership vetting and negotiation support'
      ],
      outcome: 'A reliable, compliant supply and distribution backbone — with vetted partners, valid GMP evidence, and airtight agreements in place before you enter the market.',
      bestFor: 'Exporters and importers building reliable, compliant supply partnerships'
    },
    {
      id: 'groundwork',
      slug: 'groundwork-pack',
      number: '02',
      name: 'Groundwork Pack',
      tagline: 'Start Strong. Choose the Right Markets.',
      color: '#606090',
      icon: Target,
      includes: [
        'Market feasibility mapping',
        'Product category classification (drug / supplement / food / device)',
        'Document requirement mapping for each region',
        'Country-by-country fit assessment (LATAM, GCC, Africa, Asia)',
        'SKU shortlist based on compliance difficulty',
        'Cost analysis — government fees, dossier prep, agent fees, hidden charges',
        'Step-by-step execution method with regulator-aligned milestones',
        'Regulatory timeline analysis (best-case, median, worst-case per market)',
        'Regulatory roadmap for next steps'
      ],
      outcome: 'A clear, validated direction on which products to launch and where to launch them first.',
      bestFor: 'First-time exporters and manufacturers entering new regions'
    },
    {
      id: 'gap-analysis',
      slug: 'gap-analysis-pack',
      number: '03',
      name: 'Gap Analysis Pack',
      tagline: 'Find the Gaps Before Regulators Do.',
      color: '#7090A0',
      icon: Search,
      includes: [
        'Module-wise document checklist (CTD/ACTD)',
        'Gap identification across Modules 1–5',
        'API/DMF gap check',
        'Artwork & claim compliance check',
        'Stability data alignment review',
        'GMP / CPP evidence verification',
        'Recommended actions for missing documents',
        'Submission feasibility score'
      ],
      outcome: 'A submission-ready roadmap showing exactly what is missing and how to fix it before dossier creation.',
      bestFor: 'Manufacturers with existing dossiers and anyone who has faced deficiencies before'
    },
    {
      id: 'dossier',
      slug: 'registration-dossier-pack',
      number: '04',
      name: 'Registration & Dossier Pack',
      tagline: 'Your Complete, Submission-Ready Dossier.',
      color: '#A060B0',
      icon: FileCheck,
      includes: [
        'CTD/ACTD dossier compilation (Modules 1–5)',
        'Technical write-ups, QOS, summaries',
        'API documentation integration',
        'Artwork compliance finalization',
        'Label + pack insert updates',
        'Mandatory translations (Arabic, French, Spanish, Burmese, Portuguese)',
        'Final MOH submission kit for importer'
      ],
      outcome: 'A fully prepared dossier aligned with the target country regulatory expectations — ready for importer submission.',
      bestFor: 'Exporters ready to compile and file with a Ministry of Health'
    },
    {
      id: 'lifecycle',
      slug: 'lifecycle-pack',
      number: '05',
      name: 'Lifecycle Support Pack',
      tagline: 'Stay Compliant, Year After Year.',
      color: '#708080',
      icon: RefreshCw,
      includes: [
        'MOH deficiency & query handling',
        'Variation filings (post-approval changes)',
        'Renewal management',
        'Updated artwork & pack inserts',
        'Submission follow-up with importer',
        'Full lifecycle tracker',
        'Country-specific lifecycle strategy'
      ],
      outcome: 'A smooth regulatory lifecycle that keeps your products compliant and continuously market-ready — no registration lost to an administrative oversight.',
      bestFor: 'Exporters managing multiple active registrations'
    }
  ];

  const comparisonData = [
    { feature: 'Manufacturer / importer sourcing',  sourcing: true,  groundwork: false, gap: false, dossier: false, lifecycle: false },
    { feature: 'GMP / new-site application',        sourcing: true,  groundwork: false, gap: false, dossier: false, lifecycle: false },
    { feature: 'Agreement & negotiation support',   sourcing: true,  groundwork: false, gap: false, dossier: false, lifecycle: false },
    { feature: 'Market & country mapping',          sourcing: false, groundwork: true,  gap: false, dossier: false, lifecycle: false },
    { feature: 'Regulatory pathway definition',     sourcing: false, groundwork: true,  gap: false, dossier: false, lifecycle: false },
    { feature: 'SKU prioritization',                sourcing: false, groundwork: true,  gap: false, dossier: false, lifecycle: false },
    { feature: 'Module-wise gap review',            sourcing: false, groundwork: false, gap: true,  dossier: false, lifecycle: false },
    { feature: 'API/DMF gap review',                sourcing: false, groundwork: false, gap: true,  dossier: false, lifecycle: false },
    { feature: 'Artwork & label compliance',        sourcing: false, groundwork: false, gap: true,  dossier: true,  lifecycle: true  },
    { feature: 'Dossier compilation',               sourcing: false, groundwork: false, gap: false, dossier: true,  lifecycle: false },
    { feature: 'Translations',                      sourcing: false, groundwork: false, gap: false, dossier: true,  lifecycle: true  },
    { feature: 'Query & deficiency handling',       sourcing: false, groundwork: false, gap: true,  dossier: false, lifecycle: true  },
    { feature: 'Variations / renewals',             sourcing: false, groundwork: false, gap: false, dossier: false, lifecycle: true  },
    { feature: 'Importer submission kit',           sourcing: false, groundwork: false, gap: false, dossier: true,  lifecycle: true  }
  ];

  const chooseList = [
    { condition: 'If you need manufacturing or distribution partners', pack: 'Sourcing & Partnership Pack', color: '#A07050', link: '/success-packs/sourcing-partnership-pack' },
    { condition: 'If you are starting fresh',                          pack: 'Groundwork Pack',              color: '#606090', link: '/success-packs/groundwork-pack' },
    { condition: 'If you have documents but are not submission-ready', pack: 'Gap Analysis Pack',            color: '#7090A0', link: '/success-packs/gap-analysis-pack' },
    { condition: "If you are ready to compile and file",               pack: 'Registration & Dossier Pack',  color: '#A060B0', link: '/success-packs/registration-dossier-pack' },
    { condition: 'If your product is already approved',                pack: 'Lifecycle Support Pack',       color: '#708080', link: '/success-packs/lifecycle-pack' }
  ];

  const faqs = [
    {
      q: 'What are TheApostrophe Success Packs?',
      a: 'Success Packs are structured, stage-based regulatory engagements for pharmaceutical and nutraceutical exporters entering Africa, GCC, Asia and Latin America. There are five: Sourcing & Partnership, Groundwork, Gap Analysis, Registration & Dossier, and Lifecycle Support. Each one covers a defined scope so you know exactly what you receive and when, from finding a manufacturer to maintaining an approved registration.'
    },
    {
      q: 'Which Success Pack do I need?',
      a: 'Choose by the stage you are at. If you need manufacturing or distribution partners, start with the Sourcing & Partnership Pack. If you are entering new markets, choose Groundwork. If you have documents but are not submission-ready, choose Gap Analysis. If you are ready to compile and file, choose Registration & Dossier. If your product is already approved, choose Lifecycle Support. You can also share your product list and target markets, and we will recommend the right pack.'
    },
    {
      q: 'What is the difference between the Groundwork Pack and the Gap Analysis Pack?',
      a: "The Groundwork Pack is a pre-registration toolkit that helps you decide which products to launch and in which countries, before any dossier work begins. The Gap Analysis Pack reviews documents you already have, module by module, against each country's CTD/ACTD checklist to find what is missing or non-compliant before you submit. Groundwork is about direction; Gap Analysis is about readiness."
    },
    {
      q: 'Do you help with CTD and ACTD dossier preparation?',
      a: "Yes. The Registration & Dossier Pack delivers complete CTD/ACTD dossier compilation across Modules 1 to 5, prepared to each country's specific format and submission requirements. It includes quality document alignment, compliant artwork and labelling, certified regulatory translations, and a ready-to-file importer submission kit."
    },
    {
      q: 'Which markets do you cover?',
      a: 'We support pharmaceutical and nutraceutical registrations across Africa, GCC, Asia and Latin America, including markets such as Nigeria, Kenya, Tanzania, Uganda, Ghana, Saudi Arabia, the UAE, Cambodia, Sri Lanka, the Philippines and more. Each pack is prepared country by country, aligned to the local Ministry of Health or national regulatory authority.'
    },
    {
      q: 'Do you work with both pharmaceutical and nutraceutical products?',
      a: 'Yes. We handle both pharma and nutraceutical registrations, and we keep the two tracks separate throughout, because their classification, documentation, and regulatory pathways differ. This prevents the costly rework that comes from treating a supplement like a drug, or the reverse.'
    },
    {
      q: 'Can I engage TheApostrophe on an ongoing basis instead of pack by pack?',
      a: 'Yes. The Apostrophe Retainer assigns one dedicated regulatory affairs expert to manage your entire regulatory workflow continuously, bringing the full scope of every pack together under one engagement. It suits exporters who want a regulatory department on call without hiring, training, or running an in-house team.'
    },
    {
      q: 'How do I get started?',
      a: 'Share your product list and target markets. We will assess your stage, recommend the Success Pack that fits, and outline the next steps. You can request a pack recommendation or book a call directly through this page.'
    }
  ];

  // JSON-LD schemas for SEO + AEO
  const serviceCatalogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': 'Pharmaceutical and Nutraceutical Regulatory Affairs',
    'provider': {
      '@type': 'Organization',
      'name': 'TheApostrophe',
      'url': 'https://www.theapostrophe.com'
    },
    'areaServed': ['Africa', 'GCC', 'Asia', 'Latin America'],
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Regulatory Success Packs',
      'itemListElement': packs.map((p) => ({
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': p.name,
          'description': `${p.tagline} ${p.outcome}`,
          'url': `https://www.theapostrophe.com/success-packs/${p.slug}`
        }
      }))
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((f) => ({
      '@type': 'Question',
      'name': f.q,
      'acceptedAnswer': { '@type': 'Answer', 'text': f.a }
    }))
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://www.theapostrophe.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Success Packs', 'item': 'https://www.theapostrophe.com/success-packs' }
    ]
  };

  const scrollToChoose = () => {
    const el = document.getElementById('which-pack');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Pharma Regulatory Services for Export Markets | Success Packs</title>
        <meta
          name="description"
          content="Structured regulatory affairs support for pharma and nutra exporters entering Africa, GCC, Asia and Latin America. Choose the pack that matches your stage."
        />
        <meta
          name="keywords"
          content="pharma regulatory services, regulatory affairs services for export, pharmaceutical export market entry, ACTD CTD dossier services, regulatory consultancy Africa Asia, drug registration support, nutraceutical regulatory services, MOH submission, GMP manufacturer sourcing, pharma lifecycle management"
        />
        <link rel="canonical" href="https://www.theapostrophe.com/success-packs" />

        {/* Open Graph */}
        <meta property="og:title" content="Pharma Regulatory Services for Export Markets | Success Packs" />
        <meta property="og:description" content="Structured regulatory affairs support for pharma and nutra exporters entering Africa, GCC, Asia and Latin America. Choose the pack that matches your stage." />
        <meta property="og:url" content="https://www.theapostrophe.com/success-packs" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pharma Regulatory Services for Export Markets | Success Packs" />
        <meta name="twitter:description" content="Structured, stage-based regulatory affairs for pharma & nutra exporters across Africa, GCC, Asia and LATAM." />

        {/* JSON-LD: Service catalog, FAQ, Breadcrumb */}
        <script type="application/ld+json">{JSON.stringify(serviceCatalogSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="mb-6 px-6 py-2 text-sm" style={{ backgroundColor: '#A060B0', color: 'white' }} data-testid="success-packs-badge">
            Success Packs
          </Badge>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight" style={{ color: '#303040' }} data-testid="success-packs-h1">
            Regulatory Success Packs for Pharma and Nutra Exporters
          </h1>
          <p className="text-2xl md:text-3xl font-bold mb-8" style={{ color: '#606090' }}>
            Structured, predictable, submission-ready.
          </p>
          <p className="text-lg md:text-xl mb-12 leading-relaxed max-w-4xl mx-auto" style={{ color: '#708080' }}>
            Entering a new export market should not mean guesswork. Every country has its own registration route, document format, and regulator expectations, and a single missing module can cost months. Our Success Packs give pharmaceutical and nutraceutical manufacturers a clear, stage-based path to market across Africa, GCC, Asia and Latin America, so you always know what to prepare, in what order, and for which country.
          </p>
          <p className="text-base md:text-lg mb-10 leading-relaxed max-w-3xl mx-auto" style={{ color: '#708080' }}>
            Each pack is a defined engagement built around where you are today. Start at the beginning or step in at the stage you have already reached.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={scrollToChoose}
              className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 cursor-pointer"
              style={{ backgroundColor: '#606090', color: 'white' }}
              data-testid="find-my-pack-btn"
            >
              Find My Pack
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <a
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true"
              target="_blank"
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#606090', color: '#606090' }}
                data-testid="talk-to-expert-btn"
              >
                Talk to a Regulatory Expert
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Why Packs */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(96, 96, 144, 0.08)' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-8 text-center" style={{ color: '#303040' }}>
            Why work in packs?
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-center" style={{ color: '#708080' }}>
            Regulatory documentation is not just about compiling files. It is about preparing the right documents, in the right structure, for each target country. A pack-based model removes the ambiguity: you get a predictable scope, a clear deliverable, and a direct line to a regulatory specialist at every step. That is how you reduce deficiency letters, avoid rework, and reach approval faster.
          </p>
        </div>
      </section>

      {/* The Five Success Packs - Tabbed Interface */}
      <section className="py-24 px-6" id="the-packs">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            The Five Success Packs
          </h2>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 mb-16 h-auto p-2 rounded-2xl gap-2" style={{ backgroundColor: 'rgba(112, 128, 128, 0.1)' }}>
              {packs.map((pack) => (
                <TabsTrigger
                  key={pack.id}
                  value={pack.id}
                  className="data-[state=active]:shadow-lg rounded-xl py-4 px-3 transition-all duration-300"
                  style={{
                    backgroundColor: activeTab === pack.id ? pack.color : 'transparent',
                    color: activeTab === pack.id ? 'white' : '#708080'
                  }}
                  data-testid={`tab-${pack.id}`}
                >
                  <div className="text-center">
                    <div className="font-bold text-base md:text-lg">{pack.number}</div>
                    <div className="text-xs md:text-sm font-medium leading-tight">{pack.name.replace(' Pack', '')}</div>
                  </div>
                </TabsTrigger>
              ))}
            </TabsList>

            {packs.map((pack) => (
              <TabsContent key={pack.id} value={pack.id} className="mt-0">
                <div className="grid lg:grid-cols-5 gap-12 items-start">
                  {/* Left Side - Visual */}
                  <div className="lg:col-span-2">
                    <div
                      className="rounded-3xl p-12 shadow-2xl sticky top-24"
                      style={{ backgroundColor: pack.color }}
                    >
                      <div className="w-24 h-24 rounded-2xl bg-white/20 flex items-center justify-center mb-8 mx-auto">
                        <pack.icon className="h-12 w-12 text-white" aria-hidden="true" />
                      </div>
                      <h3 className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
                        {pack.name}
                      </h3>
                      <p className="text-lg md:text-xl text-white/90 text-center italic">
                        {pack.tagline}
                      </p>
                    </div>
                  </div>

                  {/* Right Side - Details */}
                  <div className="lg:col-span-3 space-y-8">
                    <div>
                      <h4 className="text-2xl font-bold mb-6" style={{ color: '#303040' }}>
                        What It Includes
                      </h4>
                      <div className="space-y-3">
                        {pack.includes.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-4 rounded-xl hover:shadow-md transition-shadow" style={{ backgroundColor: 'white' }}>
                            <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: pack.color }} aria-hidden="true" />
                            <span className="text-base md:text-lg" style={{ color: '#708080' }}>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-8 rounded-2xl" style={{ backgroundColor: pack.color + '15' }}>
                      <h4 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: pack.color }}>
                        <Target className="h-6 w-6" aria-hidden="true" />
                        Outcome
                      </h4>
                      <p className="text-base md:text-lg leading-relaxed" style={{ color: '#708080' }}>
                        {pack.outcome}
                      </p>
                    </div>

                    <div>
                      <h4 className="text-lg font-bold mb-3" style={{ color: '#303040' }}>
                        Best For:
                      </h4>
                      <p className="text-base md:text-lg" style={{ color: '#708080' }}>
                        {pack.bestFor}
                      </p>
                    </div>

                    <Link to={`/success-packs/${pack.slug}`}>
                      <Button
                        size="lg"
                        className="w-full md:w-auto px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                        style={{ backgroundColor: pack.color, color: 'white' }}
                        data-testid={`explore-${pack.id}-btn`}
                      >
                        Explore the {pack.name}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Which pack should you choose? */}
      <section className="py-24 px-6" id="which-pack">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-center" style={{ color: '#303040' }}>
            Which pack should you choose?
          </h2>
          <p className="text-center text-lg mb-16" style={{ color: '#708080' }}>
            Simple, structured, and aligned with your stage.
          </p>
          <div className="space-y-6">
            {chooseList.map((item, index) => (
              <Link
                key={index}
                to={item.link}
                className="flex items-center justify-between p-6 md:p-8 rounded-2xl transition-all duration-300 hover:shadow-lg group"
                style={{ backgroundColor: 'white' }}
                data-testid={`choose-${index}`}
              >
                <span className="text-base md:text-xl flex-1" style={{ color: '#708080' }}>
                  {item.condition}
                </span>
                <ArrowRight className="h-6 w-6 md:h-8 md:w-8 mx-4 md:mx-6 flex-shrink-0 transition-transform group-hover:translate-x-1" style={{ color: item.color }} aria-hidden="true" />
                <span
                  className="text-lg md:text-2xl font-bold transition-all duration-300 group-hover:underline underline-offset-4 text-right"
                  style={{ color: item.color }}
                >
                  {item.pack}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Retainer Callout */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(160, 96, 176, 0.08)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="rounded-3xl p-8 md:p-12 shadow-xl" style={{ backgroundColor: 'white' }}>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#A060B0' }}
              >
                <Users className="h-10 w-10 text-white" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#303040' }}>
                  Want your entire regulatory workflow handled continuously?
                </h2>
                <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#708080' }}>
                  The <strong>Apostrophe Retainer</strong> gives you one dedicated regulatory affairs expert managing every stage on an ongoing basis, bringing the full scope of all five packs together under one engagement. You never have to hire, train, or run an in-house regulatory team.
                </p>
                <Link to="/retainer">
                  <Button
                    size="lg"
                    className="px-6 py-5 rounded-full transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: '#A060B0', color: 'white' }}
                    data-testid="learn-retainer-btn"
                  >
                    Explore the Retainer Plan
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(112, 128, 128, 0.05)' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            Compare all packs
          </h2>
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full min-w-[900px]" aria-label="Feature comparison across all Success Packs">
              <thead>
                <tr>
                  <th className="p-4 text-left text-sm md:text-base" style={{ color: '#303040' }}>Feature / Deliverable</th>
                  {packs.map((pack) => (
                    <th key={pack.id} className="p-4 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div
                          className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: pack.color }}
                        >
                          <pack.icon className="h-5 w-5 md:h-6 md:w-6 text-white" aria-hidden="true" />
                        </div>
                        <span className="text-xs md:text-sm font-bold leading-tight" style={{ color: pack.color }}>
                          {pack.name.replace(' Pack', '')}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className="border-t" style={{ borderColor: '#E8E8E8' }}>
                    <td className="p-4 font-medium text-sm md:text-base" style={{ color: '#303040' }}>
                      {row.feature}
                    </td>
                    {['sourcing', 'groundwork', 'gap', 'dossier', 'lifecycle'].map((key, i) => {
                      const colorMap = { sourcing: '#A07050', groundwork: '#606090', gap: '#7090A0', dossier: '#A060B0', lifecycle: '#708080' };
                      return (
                        <td key={i} className="p-4 text-center">
                          {row[key] ? (
                            <Check className="h-5 w-5 md:h-6 md:w-6 mx-auto" style={{ color: colorMap[key] }} aria-label="Included" />
                          ) : (
                            <X className="h-5 w-5 md:h-6 md:w-6 mx-auto text-gray-300" aria-label="Not included" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="border-t-2" style={{ borderColor: '#303040' }}>
                  <td className="p-4 font-bold text-sm md:text-base" style={{ color: '#303040' }}>Best For</td>
                  <td className="p-4 text-center text-xs md:text-sm" style={{ color: '#A07050' }}>Supply setup</td>
                  <td className="p-4 text-center text-xs md:text-sm" style={{ color: '#606090' }}>Early planning</td>
                  <td className="p-4 text-center text-xs md:text-sm" style={{ color: '#7090A0' }}>Pre-dossier</td>
                  <td className="p-4 text-center text-xs md:text-sm" style={{ color: '#A060B0' }}>Submission</td>
                  <td className="p-4 text-center text-xs md:text-sm" style={{ color: '#708080' }}>Post-approval</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ - AEO optimised */}
      <section className="py-24 px-6" id="faq">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-center" style={{ color: '#303040' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-center text-base md:text-lg mb-16" style={{ color: '#708080' }}>
            Everything you need to know about Success Packs, in one place.
          </p>
          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <div
                  key={index}
                  className="rounded-2xl overflow-hidden transition-all duration-300"
                  style={{ backgroundColor: 'white', boxShadow: isOpen ? '0 10px 30px rgba(0,0,0,0.06)' : '0 2px 8px rgba(0,0,0,0.03)' }}
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    className="w-full flex items-center justify-between text-left p-6 md:p-8 cursor-pointer"
                    aria-expanded={isOpen}
                    data-testid={`faq-toggle-${index}`}
                  >
                    <h3 className="text-base md:text-lg font-semibold pr-4" style={{ color: '#303040' }}>
                      {faq.q}
                    </h3>
                    <ChevronDown
                      className="h-5 w-5 md:h-6 md:w-6 flex-shrink-0 transition-transform duration-300"
                      style={{ color: '#606090', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }}
                      aria-hidden="true"
                    />
                  </button>
                  {isOpen && (
                    <div className="px-6 md:px-8 pb-6 md:pb-8" data-testid={`faq-answer-${index}`}>
                      <p className="text-base md:text-lg leading-relaxed" style={{ color: '#708080' }}>
                        {faq.a}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(96, 96, 144, 0.08)' }}>
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
            Ready to move forward with a structured regulatory path?
          </h2>
          <p className="text-lg md:text-xl mb-12" style={{ color: '#708080' }}>
            Share your product list and target markets, and we will recommend the Success Pack that fits your stage.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#A060B0', color: 'white' }}
                data-testid="get-pack-recommendation-btn"
              >
                Get My Pack Recommendation
                <Upload className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true"
              target="_blank"
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#606090', color: '#606090' }}
                data-testid="book-a-call-btn"
              >
                Book a Call
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
